import React, { useCallback } from 'react'

import cx from 'classnames'
import { push } from 'connected-react-router'
import BaseListItem from 'conversations/components/NewChatSidebar/BaseListItem'
import TypingLoadingDots from 'conversations/components/TypingLoadingDots'
import { useConversation } from 'conversations/hooks/useConversation'
import {
  getIsConversationUnread,
  getNumTypingUsers,
  getTypingUsersDisplayNames,
} from 'conversations/modules/conversation/selectors'
import { getCurrentUser, getOrgUserById } from 'conversations/modules/user/selectors'
import { Pill } from 'conversations/utils/pillUtil'
import { useDispatch, useSelector } from 'react-redux'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { User } from 'shared/typings/user/User'
import { UserIdType } from 'shared/typings/users/UserIdType'

import { RootState } from 'typesafe-actions'

import ConversationCloseButton from './ConversationCloseButton'

import styles from './styles.module.css'
import utilityStyles from 'conversations/css/utility.module.css'

interface Props {
  conversationId: number
  selected: boolean
  selectedStatus: Pill
  sectionID: string
  responseTimerDuration: number | null
}

const ATTACHMENT_PREVIEW = '📎 [Attachment]'

const ConversationListItemPreview = ({
  typingUsersDisplayNames,
  numTypingUsers,
  conversationPreview,
}: {
  typingUsersDisplayNames?: string
  numTypingUsers: number
  conversationPreview: string | undefined
}) => {
  return (
    <div className={styles.conversationPreview}>
      {typingUsersDisplayNames && numTypingUsers !== 0 ? (
        <div className={styles.typingDotsContainer}>
          <TypingLoadingDots className={cx(styles.typingDots, utilityStyles.darkGrayBgColor)} />
        </div>
      ) : (
        <div className={styles.conversationPreviewText}>{conversationPreview}</div>
      )}
    </div>
  )
}

const ConversationListItem: React.FC<Props> = ({
  conversationId,
  selected,
  selectedStatus,
  sectionID,
  responseTimerDuration,
}): JSX.Element => {
  const dispatch = useDispatch()

  const sectionProps = { sectionID, selectedPill: selectedStatus }
  const conversation = useConversation(conversationId, sectionProps)

  const currentUser = useSelector<RootState, User | undefined>(getCurrentUser)
  const endUserId = conversation?.endUserId

  const authorUser = useSelector<RootState, User | undefined>((state) =>
    getOrgUserById(state, conversation && conversation.lastMessageAuthorId)
  )

  const numTypingUsers = useSelector<RootState, number>(
    getNumTypingUsers(conversationId, currentUser && currentUser.id)
  )
  const typingUsersDisplayNames = useSelector<RootState, string>(
    getTypingUsersDisplayNames(conversationId, currentUser && currentUser.id)
  )

  const unread = useSelector<RootState, boolean>((state) =>
    conversation ? getIsConversationUnread(conversation.id)(state) : false
  )

  const possibleTeammateName =
    conversation &&
    conversation.lastMessageAuthorType === UserIdType.USER &&
    authorUser &&
    currentUser &&
    (currentUser.id === authorUser.id ? 'You' : authorUser.name)

  // if there's no agent preview, then there's an attachment (same logic from convo-web where it seems pretty solid)
  const conversationPreviewText =
    (conversation && conversation.agentOnlyPreview) || ATTACHMENT_PREVIEW

  const conversationPreviewWithAuthor =
    conversation && conversation.lastMessageAuthorType === UserIdType.USER
      ? `${possibleTeammateName ? possibleTeammateName : 'Teammate'}: ${conversationPreviewText}`
      : conversationPreviewText

  const unreadTestId = unread ? 'Unread' : ''

  const handleItemSelect = useCallback(() => {
    dispatch(push(`/conversations/${conversationId}`))
  }, [conversationId, dispatch])

  const canClose = conversation?.status !== ConversationStatus.CLOSED

  return (
    <BaseListItem
      itemKey={conversationId}
      inboxId={conversation?.inboxId}
      sectionID={sectionID}
      endUserId={endUserId}
      selected={selected}
      selectedStatus={selectedStatus}
      newContent={unread}
      timestamp={conversation?.updatedAt}
      responseTimerDuration={responseTimerDuration}
      details={
        <ConversationListItemPreview
          typingUsersDisplayNames={typingUsersDisplayNames}
          numTypingUsers={numTypingUsers}
          conversationPreview={conversationPreviewWithAuthor}
        />
      }
      action={canClose && <ConversationCloseButton conversationId={conversationId} />}
      onSelect={handleItemSelect}
      testId={
        selected
          ? `ConversationListItemSelected${unreadTestId}`
          : `ConversationListItem${unreadTestId}`
      }
    />
  )
}

export default React.memo(ConversationListItem)
